/**
 * EnfactInvoicesPage - Dashboard de gestion des factures Enfact
 * Permet de visualiser les ventes avec demande de facture et de créer les factures
 */
import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';
import { formatPrice } from '../utils/pos.utils';
import { logger } from '../utils/logger';

interface SaleWithInvoiceRequest {
  id: string;
  sale_number: string;
  created_at: string;
  total_ttc_cents: number;
  total_ht_cents: number;
  total_tva_cents: number;
  payment_method: string;
  invoice_requested: boolean;
  customer: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    vat_number?: string;
  } | null;
  enfact_invoice: {
    id: string;
    enfact_invoice_id?: number;
    enfact_invoice_number?: string;
    status: string;
    peppol_status?: string;
    created_at: string;
  } | null;
  vendor?: {
    name: string;
  };
}

type TabFilter = 'pending' | 'created' | 'all';

export default function EnfactInvoicesPage() {
  const { tenant } = useTenant();
  const [sales, setSales] = useState<SaleWithInvoiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('pending');
  const [creatingInvoiceFor, setCreatingInvoiceFor] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ saleId: string; success: boolean; message: string } | null>(null);

  // Verifier si Enfact est active pour ce tenant
  const enfactEnabled = Boolean(tenant?.config?.integrations?.enfact?.enabled);

  const loadSales = useCallback(async () => {
    if (!tenant?.id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await api.get(`/enfact/${tenant.id}/sales/pending-invoices`);
      setSales(data.sales || []);
    } catch (err: any) {
      logger.error('[EnfactInvoicesPage] Erreur chargement ventes:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des ventes');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    if (enfactEnabled) {
      loadSales();
    }
  }, [enfactEnabled, loadSales]);

  const handleCreateInvoice = async (saleId: string) => {
    if (!tenant?.id) return;

    setCreatingInvoiceFor(saleId);
    setActionResult(null);

    try {
      const response = await api.post(`/enfact/${tenant.id}/sales/${saleId}/invoice`);
      logger.log('[EnfactInvoicesPage] Facture creee:', response);

      setActionResult({
        saleId,
        success: true,
        message: `Facture #${response.number || response.id} creee avec succes !`,
      });

      // Recharger la liste
      await loadSales();
    } catch (err: any) {
      logger.error('[EnfactInvoicesPage] Erreur creation facture:', err);
      setActionResult({
        saleId,
        success: false,
        message: err.response?.data?.message || 'Erreur lors de la creation de la facture',
      });
    } finally {
      setCreatingInvoiceFor(null);
    }
  };

  const filteredSales = sales.filter((sale) => {
    if (activeTab === 'pending') {
      return sale.invoice_requested && !sale.enfact_invoice;
    }
    if (activeTab === 'created') {
      return sale.enfact_invoice !== null;
    }
    return true; // 'all'
  });

  const pendingCount = sales.filter((s) => s.invoice_requested && !s.enfact_invoice).length;
  const createdCount = sales.filter((s) => s.enfact_invoice !== null).length;

  if (!enfactEnabled) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Factures Enfact</h1>
          <p className="mt-2 text-gray-600">Gestion des factures electroniques PEPPOL</p>
        </div>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-amber-900 mb-2">Enfact non active</h2>
          <p className="text-amber-700">
            L'integration Enfact n'est pas activee pour ce tenant.
            Contactez l'administrateur pour activer cette fonctionnalite.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Factures Enfact</h1>
          <p className="mt-2 text-gray-600">Gestion des factures electroniques PEPPOL</p>
        </div>
        <button
          onClick={loadSales}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {loading ? 'Chargement...' : 'Actualiser'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-amber-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          A facturer
          {pendingCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-sm">
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('created')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'created'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Factures creees
          {createdCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-sm">
              {createdCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Toutes
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg text-red-800">
          ⚠️ {error}
        </div>
      )}

      {/* Action Result */}
      {actionResult && (
        <div
          className={`mb-6 p-4 rounded-lg border-2 ${
            actionResult.success
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {actionResult.success ? '✓' : '⚠️'} {actionResult.message}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-500">Chargement des ventes...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredSales.length === 0 && (
        <div className="bg-gray-50 rounded-xl border-2 border-gray-200 p-12 text-center">
          <div className="text-5xl mb-4">
            {activeTab === 'pending' ? '📄' : activeTab === 'created' ? '✓' : '📋'}
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {activeTab === 'pending'
              ? 'Aucune facture en attente'
              : activeTab === 'created'
              ? 'Aucune facture creee'
              : 'Aucune vente'}
          </h3>
          <p className="text-gray-500">
            {activeTab === 'pending'
              ? 'Les ventes avec demande de facture apparaitront ici'
              : activeTab === 'created'
              ? 'Les factures creees apparaitront ici'
              : 'Aucune vente avec demande de facture trouvee'}
          </p>
        </div>
      )}

      {/* Sales Table */}
      {!loading && filteredSales.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Vente</th>
                <th className="text-left p-4 font-semibold text-gray-700">Client</th>
                <th className="text-left p-4 font-semibold text-gray-700">Date</th>
                <th className="text-right p-4 font-semibold text-gray-700">Montant TTC</th>
                <th className="text-center p-4 font-semibold text-gray-700">Statut</th>
                <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale) => (
                <tr key={sale.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <div className="font-mono font-medium text-gray-900">{sale.sale_number}</div>
                    {sale.vendor && (
                      <div className="text-xs text-gray-500 mt-1">Vendeur: {sale.vendor.name}</div>
                    )}
                  </td>
                  <td className="p-4">
                    {sale.customer ? (
                      <div>
                        <div className="font-medium text-gray-900">
                          {sale.customer.first_name} {sale.customer.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{sale.customer.email}</div>
                        {sale.customer.vat_number && (
                          <div className="text-xs text-purple-600 mt-1">TVA: {sale.customer.vat_number}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">Pas de client</span>
                    )}
                  </td>
                  <td className="p-4 text-gray-700">
                    {new Date(sale.created_at).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="p-4 text-right">
                    <div className="font-bold text-gray-900">{formatPrice(sale.total_ttc_cents)}</div>
                    <div className="text-xs text-gray-500">
                      HT: {formatPrice(sale.total_ht_cents || (sale.total_ttc_cents - (sale.total_tva_cents || 0)))}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    {sale.enfact_invoice ? (
                      <div>
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          ✓ Facturee
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          #{sale.enfact_invoice.enfact_invoice_number || sale.enfact_invoice.enfact_invoice_id}
                        </div>
                        {sale.enfact_invoice.peppol_status && (
                          <div className="text-xs text-blue-600 mt-1">
                            PEPPOL: {sale.enfact_invoice.peppol_status}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        En attente
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    {!sale.enfact_invoice && sale.customer && (
                      <button
                        onClick={() => handleCreateInvoice(sale.id)}
                        disabled={creatingInvoiceFor === sale.id}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium text-sm transition-colors"
                      >
                        {creatingInvoiceFor === sale.id ? 'Creation...' : 'Creer facture'}
                      </button>
                    )}
                    {!sale.enfact_invoice && !sale.customer && (
                      <span className="text-xs text-red-500">Client requis</span>
                    )}
                    {sale.enfact_invoice && (
                      <span className="text-xs text-gray-400">Deja facturee</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="font-semibold text-blue-900 mb-2">Comment ca fonctionne</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>1. Dans le POS, associez un client a la vente et cochez "Demander une facture"</li>
          <li>2. Le ticket de caisse affiche "A FACTURER"</li>
          <li>3. Depuis cette page, cliquez sur "Creer facture" pour envoyer a Enfact</li>
          <li>4. La facture est automatiquement transmise via PEPPOL si le client a un numero de TVA</li>
        </ul>
      </div>
    </div>
  );
}
